import React, { useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { SettingsDA } from '../../services/admin/settings.service';
import { Company } from '../../models/company.model';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { DunningPolicy, DunningStep, DunningLateFee } from '../../models/settings.dunning-policies.model';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';

export interface AppModalDunningPolicyProps {
  companyForUser: Company | null,
  policyToEdit: DunningPolicy | null,
  loadDunningPolicies: Function,
};

let errorResponse: Error;

const emptyStep = (): DunningStep => ({
  offset_days: 7,
  channel: 'email',
  template_key: '',
  escalation_tone: 'neutral',
  late_fee: null,
});

const emptyLateFee = (): DunningLateFee => ({
  type: 'fixed',
  fixed_amount: 0,
  annual_rate: 0,
  base: 'principal',
  label: '',
});

const AppModalDunningPolicy: React.FunctionComponent<AppModalDunningPolicyProps> = ({companyForUser, policyToEdit, loadDunningPolicies}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [label, setLabel] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [stopAfterLast, setStopAfterLast] = useState(true)
  const [steps, setSteps] = useState<DunningStep[]>([emptyStep()])

  useEffect(() => {
    if (policyToEdit) {
      setLabel(policyToEdit.label)
      setIsDefault(policyToEdit.is_default)
      setStopAfterLast(policyToEdit.stop_after_last_step)
      setSteps(policyToEdit.steps.length ? policyToEdit.steps.map(s => ({...s, late_fee: s.late_fee ? {...s.late_fee} : null})) : [emptyStep()])
    } else {
      resetForm()
    }
  }, [policyToEdit])

  const resetForm = () => {
    setLabel('')
    setIsDefault(false)
    setStopAfterLast(true)
    setSteps([emptyStep()])
  }

  const addStep = () => setSteps([...steps, emptyStep()])
  const removeStep = (index: number) => setSteps(steps.filter((_, i) => i !== index))

  const updateStep = (index: number, field: keyof DunningStep, value: any) => {
    setSteps(steps.map((s, i) => i === index ? {...s, [field]: value} : s))
  }

  const toggleLateFee = (index: number) => {
    setSteps(steps.map((s, i) => i === index ? {...s, late_fee: s.late_fee ? null : emptyLateFee()} : s))
  }

  const updateLateFee = (index: number, field: keyof DunningLateFee, value: any) => {
    setSteps(steps.map((s, i) => {
      if (i !== index || !s.late_fee) return s
      return {...s, late_fee: {...s.late_fee, [field]: value}}
    }))
  }

  const validate = (): string | null => {
    if (!label.trim()) return lang.labels.dunningLabelRequired
    if (steps.length === 0) return lang.labels.dunningStepsRequired
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i]
      if (s.offset_days === null || s.offset_days === undefined || isNaN(Number(s.offset_days))) {
        return `${lang.labels.step} ${i + 1}: ${lang.labels.offsetDays} invalid`
      }
      if (s.late_fee) {
        const lf = s.late_fee
        if ((lf.type === 'fixed' || lf.type === 'both') && Number(lf.fixed_amount) <= 0) {
          return `${lang.labels.step} ${i + 1}: ${lang.labels.fixedAmount} > 0`
        }
        if ((lf.type === 'interest' || lf.type === 'both') && Number(lf.annual_rate) <= 0) {
          return `${lang.labels.step} ${i + 1}: ${lang.labels.annualRate} > 0`
        }
      }
    }
    return null
  }

  const submit = async () => {
    const validationError = validate()
    if (validationError) {
      GlobalSwal.fire({ text: validationError, icon: 'error', showConfirmButton: false, timer: 2500 })
      return
    }
    if (!companyForUser) return

    setLoadIndicator('on')

    const normalizedSteps = steps.map(s => ({
      offset_days: Number(s.offset_days),
      channel: s.channel,
      template_key: s.template_key || '',
      escalation_tone: s.escalation_tone,
      late_fee: s.late_fee ? {
        type: s.late_fee.type,
        fixed_amount: Number(s.late_fee.fixed_amount) || 0,
        annual_rate: Number(s.late_fee.annual_rate) || 0,
        base: s.late_fee.base,
        label: s.late_fee.label || '',
      } : null,
    }))

    const payload: Partial<DunningPolicy> = {
      label: label.trim(),
      is_default: isDefault,
      steps: normalizedSteps,
      stop_after_last_step: stopAfterLast,
    }

    const response = policyToEdit
      ? await SettingsDA.putDunningPolicy(companyForUser.id, policyToEdit.id, payload)
      : await SettingsDA.postDunningPolicy(companyForUser.id, payload)

    if (response.status === 200 || response.status === 201) {
      loadDunningPolicies(1)
      GlobalSwal.fire({
        title: policyToEdit ? lang.labels.successfullyUpdated : (lang.labels.successfullyCreatedCompanyParameters || 'Created'),
        icon: 'success', showConfirmButton: false, timer: 1800
      }).then(() => hideModal())
    } else {
      errorResponse = response.data
      GlobalSwal.fire({
        title: getError(errorResponse?.code, lang.code, errorResponse?.message),
        icon: 'error', confirmButtonText: lang.labels.okGotIt
      })
    }
    setLoadIndicator('off')
  }

  const hideModal = () => {
    modalHide('modal-dunning-policy')
    setTimeout(() => resetForm(), 200)
  }

  return (
    <div id="modal-dunning-policy" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2>{policyToEdit ? lang.labels.editDunningPolicy : lang.labels.addDunningPolicy}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="row mb-3">
                <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                <div className="col-lg-9">
                  <input className="form-control" type="text" value={label} onChange={(e) => setLabel(e.target.value)} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-lg-9 offset-lg-3">
                  <label className="form-check form-check-custom form-check-solid">
                    <input className="form-check-input" type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
                    <span className="form-check-label">{lang.labels.setAsDefault}</span>
                  </label>
                  <label className="form-check form-check-custom form-check-solid mt-2">
                    <input className="form-check-input" type="checkbox" checked={stopAfterLast} onChange={(e) => setStopAfterLast(e.target.checked)} />
                    <span className="form-check-label">{lang.labels.stopAfterLastStep}</span>
                  </label>
                </div>
              </div>

              <div className="separator my-4"></div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="m-0">{lang.labels.dunningSteps}</h4>
                <button type="button" className="btn btn-sm btn-light-primary" onClick={addStep}>
                  + {lang.labels.addStep}
                </button>
              </div>

              {steps.map((step, index) => (
                <div key={index} className="card card-bordered mb-3">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold">{lang.labels.step} {index + 1}</span>
                      {steps.length > 1 && (
                        <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => removeStep(index)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>

                    <div className="row mb-2">
                      <div className="col-lg-6">
                        <label className="form-label">{lang.labels.offsetDays}</label>
                        <input className="form-control" type="number" value={step.offset_days}
                          onChange={(e) => updateStep(index, 'offset_days', e.target.value)} />
                        <div className="form-text">{lang.labels.offsetDaysHint}</div>
                      </div>
                      <div className="col-lg-6">
                        <label className="form-label">{lang.labels.channel}</label>
                        <select className="form-select" value={step.channel} onChange={(e) => updateStep(index, 'channel', e.target.value)}>
                          <option value="email">Email</option>
                          <option value="in_app">In-app</option>
                          <option value="both">{lang.labels.both}</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="form-check form-check-custom form-check-solid">
                        <input className="form-check-input" type="checkbox" checked={!!step.late_fee} onChange={() => toggleLateFee(index)} />
                        <span className="form-check-label">{lang.labels.addLateFee}</span>
                      </label>
                    </div>

                    {step.late_fee && (
                      <div className="card card-bordered mt-2">
                        <div className="card-body p-3">
                          <div className="row mb-2">
                            <div className="col-lg-6">
                              <label className="form-label">{lang.labels.lateFeeType}</label>
                              <select className="form-select form-select-sm" value={step.late_fee.type} onChange={(e) => updateLateFee(index, 'type', e.target.value)}>
                                <option value="fixed">{lang.labels.lateFeeFixed}</option>
                                <option value="interest">{lang.labels.lateFeeInterest}</option>
                                <option value="both">{lang.labels.lateFeeBoth}</option>
                              </select>
                            </div>
                            <div className="col-lg-6">
                              <label className="form-label">{lang.labels.lateFeeLabel}</label>
                              <input className="form-control form-control-sm" type="text" value={step.late_fee.label} onChange={(e) => updateLateFee(index, 'label', e.target.value)} />
                            </div>
                          </div>
                          <div className="row">
                            {(step.late_fee.type === 'fixed' || step.late_fee.type === 'both') && (
                              <div className="col-lg-4">
                                <label className="form-label">{lang.labels.fixedAmount}</label>
                                <input className="form-control form-control-sm" type="number" value={step.late_fee.fixed_amount} onChange={(e) => updateLateFee(index, 'fixed_amount', e.target.value)} />
                              </div>
                            )}
                            {(step.late_fee.type === 'interest' || step.late_fee.type === 'both') && (
                              <>
                                <div className="col-lg-4">
                                  <label className="form-label">{lang.labels.annualRate}</label>
                                  <input className="form-control form-control-sm" type="number" value={step.late_fee.annual_rate} onChange={(e) => updateLateFee(index, 'annual_rate', e.target.value)} />
                                </div>
                                <div className="col-lg-4">
                                  <label className="form-label">{lang.labels.interestBase}</label>
                                  <select className="form-select form-select-sm" value={step.late_fee.base} onChange={(e) => updateLateFee(index, 'base', e.target.value)}>
                                    <option value="principal">{lang.labels.basePrincipal}</option>
                                    <option value="current">{lang.labels.baseCurrent}</option>
                                  </select>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={hideModal}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submit}>
              <span className="indicator-label">{policyToEdit ? lang.labels.save : lang.labels.create}</span>
              <span className="indicator-progress">{lang.labels.pleaseWait}<span className="spinner-border spinner-border-sm align-middle ms-2"></span></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalDunningPolicy;
